import {TreeViewBaseItem} from '@mui/x-tree-view';
import {t} from 'i18next';
import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {
  IAssignmentGroupNode,
  IMoveAssignmentGroupRequest,
} from '../../../api/types/assignment.type';
import {
  useAssignmentTree,
  useDeleteAssignmentGroup,
  useMoveAssignmentGroup,
} from '../../../hooks/assignments/useAssignments';
import {RootState} from '../../../redux/store';
import {ToastType} from '../../../shared/constants';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {CustomMenuItem} from '../../common/arr-menu/ArrMenu';
import ArrModal from '../../common/arr-modal/ArrModal';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import ArrTreeView from '../../common/arr-tree-view/ArrTreeView';
import {manageAssignmentsMoreMenu} from '../AssignmentsUtils';
import {
  clearBackSourceTabIndex,
  setAssignmentTree,
  setSelectedData,
} from '../../../redux/slice/assignmentSlice';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {Box} from '@mui/material';
interface IManageAssignmentTreeProps {
  isToggleActive: boolean;
  getAssignmentGroupId: (item: string) => void;
}
const ManageAssignmentsTree: React.FC<IManageAssignmentTreeProps> = ({
  isToggleActive,
  getAssignmentGroupId,
}) => {
  const dispatch = useDispatch();
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const {
    data: treeData,
    refetch: refetchTreeData,
    isLoading: isLoadingTreeData,
  } = useAssignmentTree(isToggleActive ? 1 : 0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openMoveModal, setOpenMoveModal] = useState<boolean>(false);

  const assignmentListData = useSelector(
    (state: RootState) => state.assignment.assignmentList
  );

  const {
    mutate: mutateDeleteAssignmentGroup,
    error: errorDeleteAssignmentGroup,
    isSuccess: isSuccessDeleteAssignmentGroup,
  } = useDeleteAssignmentGroup();

  const {
    mutate: mutateMoveAssignmentGroup,
    reset: resetMoveAssignmentGroup,
    isPending: isPendingMoveAssignmentGroup,
  } = useMoveAssignmentGroup();

  useEffect(() => {
    dispatch(setAssignmentTree(treeData ?? []));
  }, [treeData]);

  useEffect(() => {
    if (errorDeleteAssignmentGroup) {
      triggerToast(
        t('assignment.manageAssignmentsTree.errorDeleteAssignmentGroup'),
        ToastType.Error
      );
    } else if (isSuccessDeleteAssignmentGroup) {
      triggerToast(
        t('assignment.manageAssignmentsTree.successAssignmentGroup'),
        ToastType.Success
      );
      setOpenDeleteModal(false);
      refetchTreeData();
    }
  }, [isSuccessDeleteAssignmentGroup, errorDeleteAssignmentGroup]);

  const transformToTreeItems = (
    flatList: IAssignmentGroupNode[] | undefined
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    if (!Array.isArray(flatList)) return [];

    const nodeMap = new Map<string, TreeViewBaseItem<ExtendedTreeItemProps>>();
    const tree: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

    // First pass: create all nodes and store in map
    for (const item of flatList) {
      nodeMap.set(item.id, {
        label: item.text,
        count: item.documentCount.toString(),
        isCascadeEnabled: item.li_attr?.isCascadeEnabled,
        showMoreOptions: true,
        children: [],
        ...item,
      });
    }
    // Second pass: build tree
    for (const node of flatList) {
      const current = nodeMap.get(node.id);
      if (!current) continue;

      if (node.parent && nodeMap.has(node.parent)) {
        const parent = nodeMap.get(node.parent)!;
        (parent.children ??= []).push(current);
      } else {
        tree.push(current);
      }
    }

    return tree;
  };
  const transformedTree = transformToTreeItems(treeData);
  useEffect(() => {
    // Auto-select first node if available
    if (transformedTree.length > 0) {
      setSelectedItem(transformedTree[0].id);
      getAssignmentGroupId(transformedTree[0].id);
    }
  }, [treeData]);

  const handleSelected = (value: string[] | null) => {
    if (Array.isArray(value) && value.length > 0) {
      setSelectedItem(value[0]);
      getAssignmentGroupId(value[0]);
    } else {
      setSelectedItem(null);
    }
  };

  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const navigate = useNavigate();
  const handleActionMenuItemClick = (item: CustomMenuItem) => {
    if (item.id == 'New_Assignment_Group') {
      dispatch(
        setSelectedData({
          type: 'assignmentGroup',
          mode: 'create',
          assignmentId: 0,
          assignmentGroupId: Number(selectedItem) ?? -1,
        })
      );
      navigate('/Assignment/Create');
    } else if (item.id == 'Edit_Assignment_Group') {
      if (Number(selectedItem) === -1) {
        triggerToast(
          t('assignment.manageAssignmentsTree.editRoot'),
          ToastType.Warning
        );
        return;
      }
      const parentGroupId = treeData?.find((item) => item.id == selectedItem);
      dispatch(clearBackSourceTabIndex());
      dispatch(
        setSelectedData({
          type: 'assignmentGroup',
          mode: 'edit',
          assignmentId: Number(selectedItem) ?? 0,
          assignmentGroupId: Number(parentGroupId?.parent ?? -1),
        })
      );
      navigate('/Assignment/Edit');
    } else if (item.id == 'Delete_Assignment_Group') {
      if (Number(selectedItem) === -1) {
        triggerToast(
          t('assignment.manageAssignmentsTree.deleteRoot'),
          ToastType.Warning
        );
      } else if (treeData?.find((item) => item.parent == selectedItem)) {
        triggerToast(
          t('assignment.manageAssignmentsTree.deleteParent'),
          ToastType.Warning
        );
      } else if (assignmentListData.totalRecords) {
        triggerToast(
          t('assignment.manageAssignmentsTree.deleteAssignmentGroup'),
          ToastType.Warning
        );
      } else {
        setOpenDeleteModal(true);
      }
    }
  };

  const handleConfirmDelete = () => {
    mutateDeleteAssignmentGroup({
      selectedGroupId: Number(selectedItem),
    });
  };

  const movePayload = useRef<IMoveAssignmentGroupRequest | null>(null);

  const handleConfirmMove = () => {
    mutateMoveAssignmentGroup(movePayload.current!, {
      onSuccess: () => {
        triggerToast(t('assignment.moveSuccess'), ToastType.Success);
        setOpenMoveModal(false);
        resetMoveAssignmentGroup();
        refetchTreeData();
      },
      onError: () => {
        triggerToast(t('assignment.moveError'), ToastType.Error);
        resetMoveAssignmentGroup();
        refetchTreeData();
      },
    });
  };

  return (
    <>
      {isLoadingTreeData || isPendingMoveAssignmentGroup ? (
        <Box className="loading-container-assignment">
          <ArrLoadingSpinner />
        </Box>
      ) : (
        <ArrTreeView
          setSelectedItem={handleSelected}
          itemsList={transformedTree}
          selectedItem={selectedItem}
          handleNoResultMessage={t('common.noResultFound')}
          allowGlobalSelect={false}
          multipleSelection={false}
          isShowCheckbox={false}
          showActions={true}
          isShowCountLabel={false}
          menuDirection="bottom"
          showTooltip
          MoreMenuOptions={manageAssignmentsMoreMenu()}
          onMenuItemClick={handleActionMenuItemClick}
          enableDragAndDrop={true}
          afterDragEnd={(source, target) => {
            movePayload.current = {
              assignmentId: Number(source.id),
              newParentId: Number(target.id),
            };
            if (source.id === target.id) {
              return;
            } else if (target.isCascadeEnabled) {
              setOpenMoveModal(true);
            } else {
              handleConfirmMove();
            }
          }}
          expandSelect={false}
        />
      )}
      <ArrModal
        showModal={openMoveModal}
        title={t('common.move')}
        description={t('assignment.moveHelpText')}
        ariaLabel={t('common.move')}
        ariaDescription={t('assignment.moveHelpText')}
        onClose={() => setOpenMoveModal(false)}
        dataTestId="manage-assignments-tree-modal"
        buttons={[
          {
            id: 'cancel-move',
            label: t('common.no'),
            onClick: () => setOpenMoveModal(false),
            variant: 'secondary',
            dataTestId: 'mat-no-btn',
          },
          {
            id: 'confirm-move',
            label: t('common.yes'),
            onClick: () => {
              handleConfirmMove();
            },
            variant: 'primary',
            loading: isPendingMoveAssignmentGroup,
            dataTestId: 'mat-yes-btn',
          },
        ]}
      />
      <ArrModal
        showModal={openDeleteModal}
        title={t('common.delete')}
        description={t('common.areYouSure')}
        ariaLabel={t('common.delete')}
        ariaDescription={t('common.areYouSure')}
        onClose={() => setOpenDeleteModal(false)}
        dataTestId="mat-open-delete-modal"
        buttons={[
          {
            id: 'cancel-delete',
            label: t('common.cancel'),
            onClick: () => setOpenDeleteModal(false),
            variant: 'secondary',
            dataTestId: 'matod-cancel-btn',
          },
          {
            id: 'confirm-delete',
            label: t('common.ok'),
            onClick: () => {
              handleConfirmDelete();
            },
            variant: 'primary',
            dataTestId: 'matod-ok-btn',
          },
        ]}
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
    </>
  );
};

export default ManageAssignmentsTree;
